import React from "react";
import { useTranslation } from "react-i18next";
import { GripVertical, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SortableList } from "@/components/generic/SortableList";
import { DestinationProductCard } from "./DestinationProductCard";
import { Badge } from "@/components/ui/badge";
import { getCuratedLimitState } from "../utils/curatedLimit";
import type { PartnerDestinationProduct } from "@/types/partner-destination";

interface DestinationCuratedPanelProps {
  items: PartnerDestinationProduct[];
  onReorder: (items: PartnerDestinationProduct[]) => void;
  onRemove: (productCode: string) => void;
  canWrite: boolean;
}

export const DestinationCuratedPanel: React.FC<
  DestinationCuratedPanelProps
> = ({ items, onReorder, onRemove, canWrite }) => {
  const { t } = useTranslation();
  const limit = getCuratedLimitState(items.length);

  const renderCard = (
    product: PartnerDestinationProduct,
    isDragging?: boolean,
  ) => (
    <DestinationProductCard
      product={product}
      isDragging={isDragging}
      className={canWrite ? "cursor-grab active:cursor-grabbing" : undefined}
      action={
        <div className="flex items-center gap-1">
          {canWrite && (
            <GripVertical className="text-muted-foreground size-4" />
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon_sm"
            disabled={!canWrite}
            aria-label={t("partners.admin.destinations.detail.remove")}
            title={t("partners.admin.destinations.detail.remove")}
            onClick={(event) => {
              event.stopPropagation();
              onRemove(product.productCode);
            }}
          >
            <X className="size-4" />
          </Button>
        </div>
      }
    />
  );

  const renderList = (): React.JSX.Element => {
    if (items.length === 0) {
      return (
        <p className="text-muted-foreground rounded-xl border border-dashed p-6 text-center text-sm">
          {t("partners.admin.destinations.detail.curatedListEmpty")}
        </p>
      );
    }

    if (!canWrite) {
      return (
        <div className="flex flex-col gap-2">
          {items.map((product) => (
            <React.Fragment key={product.productCode}>
              {renderCard(product)}
            </React.Fragment>
          ))}
        </div>
      );
    }

    return (
      <SortableList
        items={items}
        direction="column"
        keyExtractor={(item) => item.productCode}
        onReorder={onReorder}
        className="flex flex-col gap-2"
        renderItem={renderCard}
      />
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-foreground text-lg font-semibold">
          {t("partners.admin.destinations.detail.curatedListTitle")}
        </h2>
        <Badge
          variant={limit.isFull ? "destructive" : "secondary"}
          data-testid="curated-limit-counter"
        >
          {t("partners.admin.destinations.detail.curatedLimitCounter", {
            count: limit.count,
            max: limit.max,
          })}
        </Badge>
      </div>
      <p className="text-muted-foreground text-xs">
        {t("partners.admin.destinations.detail.curatedLimitHint", {
          max: limit.max,
        })}
      </p>

      {renderList()}
    </div>
  );
};
